import React from 'react';

export interface RestartAgentConfirmDialogProps {
  open: boolean;
  agentLabel: string;
  onRestart: () => void;
  onDontRestart: () => void;
  onCancel: () => void;
  /** True while the save request is in flight after choosing Restart or Don&apos;t restart. */
  isBusy?: boolean;
  /** Shown above the actions when the deferred save fails. */
  errorMessage?: string | null;
}

/**
 * Confirmation before saving when mailbox ID or allowlist changed while the agent is running.
 * Nothing is persisted until the user chooses Restart or Don&apos;t restart.
 */
export function RestartAgentConfirmDialog({
  open,
  agentLabel,
  onRestart,
  onDontRestart,
  onCancel,
  isBusy = false,
  errorMessage,
}: RestartAgentConfirmDialogProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isBusy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="restart-agent-dialog-title"
      aria-describedby="restart-agent-dialog-desc"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col overflow-hidden border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-5 pb-2">
          <h2
            id="restart-agent-dialog-title"
            className="text-base font-semibold text-gray-900"
          >
            Apply configuration now?
          </h2>
          <p id="restart-agent-dialog-desc" className="mt-2 text-sm text-gray-600 leading-relaxed">
            Mailbox user ID and allowed email             changes only take effect after{' '}
            <span className="font-medium text-gray-800">{agentLabel}</span> is restarted. Nothing is
            saved until you choose below.
          </p>
        </div>

        {errorMessage ? (
          <div className="px-6 pb-2">
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {errorMessage}
            </p>
          </div>
        ) : null}

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/80">
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              type="button"
              disabled={isBusy}
              onClick={onCancel}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-left sm:text-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              Cancel
            </button>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 w-full sm:w-auto">
              <button
                type="button"
                disabled={isBusy}
                onClick={onDontRestart}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBusy ? 'Saving…' : "Don't restart"}
              </button>
              <button
                type="button"
                disabled={isBusy}
                onClick={onRestart}
                className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBusy ? 'Saving…' : 'Restart'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
